/**
 * Matriz de fontes aceitas por modalidade de pagamento prioritário (spec antecipe-registration
 * §2/§6, Q15/Q26). Mora num `.ts` pelo mesmo motivo de `sourcePrecedence.ts`: named exports
 * de `.vue` quebram o `check-types`.
 *
 * Semântica do backend: `null` (ou chave ausente) = todas as fontes. A tela mostra a matriz
 * materializada, mas o save devolve o valor cru enquanto o usuário não mexe — o gate de
 * `attendance_registration_configuring` compara com o gravado (lição A1/1.04).
 */
import { EffectiveSourceKind } from '@/constants/ds-attendance.constants'
import { PAYMENT_MODALITIES } from '@/constants/payment-eligibility.constants'
import type { PaymentModality, PaymentSourceAcceptance } from '@/types/payment-eligibility.types'

/** `MANUAL_LEGACY`/`LEGACY` ficam fora: são sempre aceitos (histórico). */
export const ACCEPTANCE_SOURCE_KINDS: EffectiveSourceKind[] = [
  EffectiveSourceKind.APP,
  EffectiveSourceKind.SHEET,
  EffectiveSourceKind.EXTERNAL_APP,
  EffectiveSourceKind.ACTIVITY_RECORD,
  EffectiveSourceKind.AUTO_PERMANENCE
]

export type PaymentSourceAcceptanceMatrix = Record<PaymentModality, EffectiveSourceKind[]>

const canonical = (kinds: readonly EffectiveSourceKind[]): EffectiveSourceKind[] =>
  ACCEPTANCE_SOURCE_KINDS.filter((kind) => kinds.includes(kind))

export function materializePaymentSourceAcceptance(
  raw?: PaymentSourceAcceptance | null
): PaymentSourceAcceptanceMatrix {
  const pick = (modality: PaymentModality): EffectiveSourceKind[] => {
    const kinds = raw?.[modality]
    return Array.isArray(kinds) ? canonical(kinds) : [...ACCEPTANCE_SOURCE_KINDS]
  }
  return { ANTECIPE: pick('ANTECIPE'), IN_CASH: pick('IN_CASH') }
}

/** Modalidade com todas as fontes é OMITIDA; nenhuma customizada = `null`. */
export function serializePaymentSourceAcceptance(
  matrix: PaymentSourceAcceptanceMatrix
): PaymentSourceAcceptance | null {
  const result: PaymentSourceAcceptance = {}
  for (const modality of PAYMENT_MODALITIES) {
    const kinds = canonical(matrix[modality])
    if (kinds.length !== ACCEPTANCE_SOURCE_KINDS.length) result[modality] = kinds
  }
  return Object.keys(result).length ? result : null
}

const sameMatrix = (a: PaymentSourceAcceptanceMatrix, b: PaymentSourceAcceptanceMatrix): boolean =>
  PAYMENT_MODALITIES.every((modality) => {
    const left = canonical(a[modality])
    const right = canonical(b[modality])
    return left.length === right.length && left.every((kind, index) => kind === right[index])
  })

/** Sempre aceitas pelo backend; podem sair do objeto sem mudar o resultado. */
const HISTORY_SOURCE_KINDS: string[] = [
  EffectiveSourceKind.MANUAL_LEGACY,
  EffectiveSourceKind.LEGACY
]

/** Fontes gravadas que este web não exibe (backend mais novo): a tela não as mostra. */
const unknownKinds = (kinds: unknown): EffectiveSourceKind[] =>
  Array.isArray(kinds)
    ? kinds.filter(
        (kind): kind is EffectiveSourceKind =>
          !ACCEPTANCE_SOURCE_KINDS.includes(kind) && !HISTORY_SOURCE_KINDS.includes(kind)
      )
    : []

/**
 * Tocado: serializa a matriz e devolve, em cada modalidade restrita, as fontes que a tela
 * não conhece. Sem isso um clique em qualquer caixa deixaria de aceitar uma fonte nova do
 * backend sem que o usuário a visse. Modalidade omitida já aceita tudo, inclusive a nova.
 */
export function paymentSourceAcceptanceForSave(
  loaded: PaymentSourceAcceptance | null,
  matrix: PaymentSourceAcceptanceMatrix
): PaymentSourceAcceptance | null {
  if (sameMatrix(matrix, materializePaymentSourceAcceptance(loaded))) return loaded
  const saved = serializePaymentSourceAcceptance(matrix)
  if (!saved) return saved
  for (const modality of PAYMENT_MODALITIES) {
    const kept = saved[modality]
    const unknown = unknownKinds(loaded?.[modality])
    if (kept && unknown.length) saved[modality] = [...kept, ...unknown]
  }
  return saved
}

export function setPaymentSourceAccepted(
  matrix: PaymentSourceAcceptanceMatrix,
  modality: PaymentModality,
  kind: EffectiveSourceKind,
  accepted: boolean
): PaymentSourceAcceptanceMatrix {
  const others = matrix[modality].filter((current) => current !== kind)
  return { ...matrix, [modality]: canonical(accepted ? [...others, kind] : others) }
}

export function modalitiesWithoutSource(matrix: PaymentSourceAcceptanceMatrix): PaymentModality[] {
  return PAYMENT_MODALITIES.filter((modality) => matrix[modality].length === 0)
}
